import React, { useMemo, useRef, useState } from 'react';
import { Pencil, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  DISPLAY_NAME_MAX_LENGTH,
  formatMemberSince,
  getEditableNameParts,
  getInitials,
  NAME_PART_MAX_LENGTH,
} from './accountShared';

function combinedNameLength(first: string, last: string): number {
  const f = first.trim();
  const l = last.trim();
  if (!f && !l) return 0;
  if (!f) return l.length;
  if (!l) return f.length;
  return f.length + 1 + l.length;
}

const cardClass =
  'rounded-xl border border-theme-border/80 dark:border-white/10 bg-white dark:bg-white/[0.04] overflow-hidden';

/** Profile summary + edit-name modal (Account hub / former Profile page). */
export const AccountProfileCard: React.FC<{ className?: string }> = ({ className }) => {
  const { profile, user, updateProfile } = useAuth();

  const [showNameModal, setShowNameModal] = useState(false);
  const [firstDraft, setFirstDraft] = useState('');
  const [lastDraft, setLastDraft] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameSaving, setNameSaving] = useState(false);
  const firstInputRef = useRef<HTMLInputElement>(null);

  const accountEmail = profile?.email ?? user?.email ?? '';
  const userMeta = user?.user_metadata as Record<string, unknown> | undefined;

  const displayName = useMemo(() => {
    const pf = profile?.first_name?.trim() ?? '';
    const pl = profile?.last_name?.trim() ?? '';
    if (pf || pl) return [pf, pl].filter(Boolean).join(' ');
    const full = typeof userMeta?.full_name === 'string' ? userMeta.full_name.trim() : '';
    if (full) return full;
    const n = typeof userMeta?.name === 'string' ? userMeta.name.trim() : '';
    if (n) return n;
    if (accountEmail) return accountEmail.split('@')[0] || 'Member';
    return 'Member';
  }, [profile?.first_name, profile?.last_name, userMeta, accountEmail]);

  const memberSince = formatMemberSince(profile?.created_at);

  const openNameModal = () => {
    setNameError(null);
    const { first, last } = getEditableNameParts(
      profile,
      userMeta,
      displayName !== 'Member' ? displayName : undefined,
    );
    setFirstDraft(first);
    setLastDraft(last);
    setShowNameModal(true);
    requestAnimationFrame(() => firstInputRef.current?.focus());
  };

  const closeNameModal = () => {
    if (nameSaving) return;
    setShowNameModal(false);
    setNameError(null);
    setFirstDraft('');
    setLastDraft('');
  };

  const saveName = async () => {
    const first = firstDraft.trim();
    const last = lastDraft.trim();
    if (!first) {
      setNameError('First name is required.');
      return;
    }
    if (first.length > NAME_PART_MAX_LENGTH || last.length > NAME_PART_MAX_LENGTH) {
      setNameError(`Use at most ${NAME_PART_MAX_LENGTH} characters per field.`);
      return;
    }
    const full = [first, last].filter(Boolean).join(' ');
    if (full.length > DISPLAY_NAME_MAX_LENGTH) {
      setNameError(`Full name must be at most ${DISPLAY_NAME_MAX_LENGTH} characters.`);
      return;
    }
    setNameError(null);
    setNameSaving(true);
    try {
      await updateProfile({ first_name: first, last_name: last });
      closeNameModal();
    } catch (e) {
      setNameError(e instanceof Error ? e.message : 'Could not save. Try again.');
    } finally {
      setNameSaving(false);
    }
  };

  return (
    <>
      <div className={`${cardClass} ${className ?? ''}`.trim()}>
        <div className="px-4 py-4 flex gap-3 items-center">
          <div
            className="flex-shrink-0 w-11 h-11 rounded-full bg-theme-muted/70 dark:bg-white/10 flex items-center justify-center text-xs font-semibold text-theme-text dark:text-theme-off-white"
            aria-hidden
          >
            {getInitials(displayName)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-theme-text dark:text-theme-off-white truncate">
                  {displayName}
                </p>
                <p className="text-[13px] font-medium text-theme-text/65 dark:text-theme-off-white/60 mt-0.5 truncate">
                  {accountEmail || '…'}
                </p>
                {memberSince && (
                  <p className="text-xs text-theme-text/40 dark:text-theme-off-white/35 mt-2.5 truncate">
                    Member since {memberSince}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={openNameModal}
                className="p-1.5 rounded-md text-theme-text/55 hover:bg-theme-muted/40 dark:hover:bg-white/10 shrink-0"
                aria-label="Edit name"
              >
                <Pencil className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {showNameModal && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-theme-text/50 dark:bg-black/60 backdrop-blur-sm p-4"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeNameModal();
          }}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-theme-border dark:border-white/15 bg-white dark:bg-white/10 p-6 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-name-modal-title"
          >
            <div className="relative mb-4 flex min-h-10 items-center justify-center">
              <button
                type="button"
                onClick={closeNameModal}
                disabled={nameSaving}
                className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full p-2 text-theme-text/55 transition-colors hover:bg-theme-muted/50 hover:text-theme-text disabled:opacity-50 dark:text-theme-off-white/60 dark:hover:bg-white/10 dark:hover:text-theme-off-white"
                aria-label="Close"
              >
                <X className="h-5 w-5" strokeWidth={2.25} aria-hidden />
              </button>
              <h2
                id="edit-name-modal-title"
                className="px-10 text-center text-base font-semibold text-theme-text dark:text-theme-off-white"
              >
                Edit name
              </h2>
            </div>
            <div className="space-y-3">
              <div>
                <label
                  htmlFor="profile-first-name"
                  className="block text-xs font-medium text-theme-text/70 dark:text-theme-off-white/65 mb-1"
                >
                  First name
                </label>
                <input
                  ref={firstInputRef}
                  id="profile-first-name"
                  type="text"
                  autoComplete="given-name"
                  maxLength={NAME_PART_MAX_LENGTH}
                  value={firstDraft}
                  onChange={(e) => setFirstDraft(e.target.value)}
                  disabled={nameSaving}
                  className="w-full rounded-lg border border-theme-border dark:border-white/15 bg-theme-off-white/40 dark:bg-black/20 px-3 py-2.5 text-sm text-theme-text dark:text-theme-off-white focus:outline-none focus:ring-2 focus:ring-theme-text/20 dark:focus:ring-white/20"
                />
              </div>
              <div>
                <label
                  htmlFor="profile-last-name"
                  className="block text-xs font-medium text-theme-text/70 dark:text-theme-off-white/65 mb-1"
                >
                  Last name
                </label>
                <input
                  id="profile-last-name"
                  type="text"
                  autoComplete="family-name"
                  maxLength={NAME_PART_MAX_LENGTH}
                  value={lastDraft}
                  onChange={(e) => setLastDraft(e.target.value)}
                  disabled={nameSaving}
                  className="w-full rounded-lg border border-theme-border dark:border-white/15 bg-theme-off-white/40 dark:bg-black/20 px-3 py-2.5 text-sm text-theme-text dark:text-theme-off-white focus:outline-none focus:ring-2 focus:ring-theme-text/20 dark:focus:ring-white/20"
                />
              </div>
            </div>
            {nameError && (
              <p className="mt-3 text-sm text-red-600 dark:text-red-400 text-center" role="alert">
                {nameError}
              </p>
            )}
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => void saveName()}
                disabled={nameSaving || combinedNameLength(firstDraft, lastDraft) > DISPLAY_NAME_MAX_LENGTH}
                className="w-full sm:w-auto px-4 py-2.5 rounded-xl text-sm font-medium bg-theme-text dark:bg-theme-off-white text-theme-off-white dark:text-theme-bg-dark hover:opacity-90 disabled:opacity-50"
              >
                {nameSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
